<template>
    <div>
        <div class="relative">
            <Transition
                name="fast-fade"
                mode="out-in">
                <div
                    v-if="activeRegion"
                    :class="regionInfoClasses"
                    class="absolute flex select-none flex-col justify-center overflow-hidden text-nowrap rounded-12 border border-accent-300 bg-white p-3 text-24/100 font-semibold text-ash shadow-map transition-all duration-200 xl:p-5"
                    @mousemove="handleChangeRegion(regionSlug)">
                    <h3>{{ activeRegion.name }}</h3>
                    <div class="mt-3">
                        <NuxtLink
                            v-if="activeRegion.count_portfolios > 0"
                            :to="'/portfolio?region_id=' + activeRegion.id"
                            class="text-accent underline underline-offset-8 transition-colors duration-150 hover:text-accent-400">
                            {{ getProjectWord(activeRegion.count_portfolios) }}
                        </NuxtLink>
                        <p
                            v-else
                            class="text-ash-300">
                            Нет проектов
                        </p>
                    </div>
                </div>
            </Transition>
            <MapSvg
                v-model="regionSlug"
                :size
                class="overflow-visible"
                @change-region="handleChangeRegion" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'

import MapSvg from '~/components/particles/map-section/MapSvg.vue'
import {
    type MapRegion,
    RegionSlugById,
} from '~/components/particles/map-section/map-section.types'

import { plural } from '~/utils/plural'

const regionSlug = defineModel({
    type: [String, null] as PropType<string | null>,
    default: null,
})

const props = defineProps({
    regions: {
        type: Array as PropType<MapRegion[]>,
        default: () => [],
    },
    size: {
        type: String as PropType<'medium' | 'big'>,
        default: 'big',
    },
})

const probablyRegionSlug: Ref<string | null> = ref(null)

const regionInfoClasses: ComputedRef<string> = computed(() => {
    switch (regionSlug.value) {
        case 'ural':
            return 'left-[40%] top-1/2 xl:w-[11rem]'
        case 'south':
            return 'left-0 top-[50%] xl:w-[12rem]'
        case 'privolzsky':
            return 'left-[22%] top-[45%] xl:w-[16.5rem]'
        case 'north-west':
            return 'left-1/4 top-[20%] xl:w-[13rem]'
        case 'east':
            return 'left-[55%] top-[65%] w-[21rem]'
        case 'central':
            return 'left-[25%] top-[35%] w-[16.5rem]'
        case 'sng':
            return 'left-[20%] top-3/4 w-[12.5rem]'
        default:
            return ''
    }
})

const activeRegion = computed<MapRegion | null>(() => {
    return (
        props.regions.find(
            (region: MapRegion) =>
                region.id !== null &&
                RegionSlugById[region.id] === regionSlug.value
        ) || null
    )
})

const changeRegion = useDebounceFn(() => {
    if (regionSlug.value === probablyRegionSlug.value) return
    regionSlug.value = probablyRegionSlug.value
}, 200)

function handleChangeRegion(region: string | null) {
    if (probablyRegionSlug.value === region) return

    probablyRegionSlug.value = region
    changeRegion()
}

function getProjectWord(count: number) {
    return count + ' ' + plural(count, ['проект', 'проекта', 'проектов'])
}
</script>
